/**
 * calendarStore — Zustand store for calendar month navigation.
 *
 * Holds the currently viewed {year, month} as ephemeral UI state.
 * Month is 0-indexed (JS Date convention: Jan=0, Dec=11).
 *
 * Initialization uses local-time getters (getFullYear/getMonth) —
 * consistent with getTodayISO() convention to show the user's local
 * calendar date, not UTC. See Pitfall 5 in 03-RESEARCH.md.
 *
 * Year-boundary wrapping uses the JS Date constructor naturally:
 *   new Date(2026, 12, 1) → Jan 2027  (Dec → Jan rolls year forward)
 *   new Date(2026, -1, 1)  → Dec 2025 (Jan → Dec rolls year back)
 * No manual modulo needed.
 */
import { create } from 'zustand';

interface CalendarState {
  /** Currently viewed year (e.g. 2026) */
  year: number;
  /** Currently viewed month, 0-indexed (Jan=0, Dec=11) */
  month: number;
  /** Navigate to the previous month, wrapping year boundaries correctly */
  prevMonth: () => void;
  /** Navigate to the next month, wrapping year boundaries correctly */
  nextMonth: () => void;
  /** Jump back to the current (local) month */
  goToToday: () => void;
  /** Whether the left summary sidebar is visible (calendar view). */
  summaryOpen: boolean;
  /** Toggle the summary sidebar open/closed. */
  toggleSummary: () => void;
  /** Close the summary sidebar (used by the native Android back-button handler). */
  closeSummary: () => void;
  /** Currently selected date (YYYY-MM-DD) — drives the inline DayDetailPanel below the grid. */
  selectedDate: string;
  /** Select a date to display in the inline DayDetailPanel. */
  setSelectedDate: (d: string) => void;
  /** Whether the mobile draggable bottom sheet (DayDetailPanel) is visible. */
  dayDetailOpen: boolean;
  /** Select a date AND open the mobile bottom sheet to show its details. */
  openDayDetail: (d: string) => void;
  /** Close the mobile bottom sheet. */
  closeDayDetail: () => void;
  /** Whether the in-place add-entry dialog is open — also driven by the iOS home-screen quick action. */
  addEntryOpen: boolean;
  /** Open the add-entry dialog (desktop button, mobile FAB, and the iOS quick action). */
  openAddEntry: () => void;
  /** Close the add-entry dialog. */
  closeAddEntry: () => void;
}

/** Derive YYYY-MM-DD using local getters (Pitfall 1: avoids UTC off-by-one from toISOString). */
function getLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const useCalendarStore = create<CalendarState>((set) => ({
  // Local-time getters — never getUTCFullYear/getUTCMonth (Pitfall 5)
  year: new Date().getFullYear(),
  month: new Date().getMonth(),

  prevMonth: () =>
    set((s) => {
      // JS Date naturally wraps: new Date(2026, -1, 1) → Dec 2025
      const d = new Date(s.year, s.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    }),

  nextMonth: () =>
    set((s) => {
      // JS Date naturally wraps: new Date(2026, 12, 1) → Jan 2027
      const d = new Date(s.year, s.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    }),

  goToToday: () => {
    // Local-time getters — show the user's current calendar month (Pitfall 5)
    const now = new Date();
    set({ year: now.getFullYear(), month: now.getMonth() });
  },

  // Open by default on desktop (lg+), collapsed on mobile so the summary sits behind the
  // header hamburger rather than taking vertical space.
  summaryOpen: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,

  toggleSummary: () => set((s) => ({ summaryOpen: !s.summaryOpen })),

  closeSummary: () => set({ summaryOpen: false }),

  // Local-time getters — never toISOString (Pitfall 1)
  selectedDate: getLocalDateStr(new Date()),

  setSelectedDate: (d) => set({ selectedDate: d }),

  dayDetailOpen: false,

  openDayDetail: (d) => set({ selectedDate: d, dayDetailOpen: true }),

  closeDayDetail: () => set({ dayDetailOpen: false }),

  addEntryOpen: false,

  openAddEntry: () => set({ addEntryOpen: true }),

  closeAddEntry: () => set({ addEntryOpen: false }),
}));
